import Button from '@/components/Buttons/Button';
import IconButton from '@/components/Buttons/IconButton';
import BootstrapDialog from '@/components/Dialog/BootstrapDialog';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import Switch from '@/components/Switch/Switch';
import TextField from '@/components/TextField/TextField';
import { useUpsertSecret } from '@/hooks/external-keys/external-keys';
import ArrowDown from '@/images/ArrowDown.svg';
import ArrowLinearRight from '@/images/arrows/ArrowLinearRight.svg';
import Key from '@/images/Key.svg';
import { DEFAULT_EXTERNAL_ENDPOINT } from '@/pages/agents/utils';
import theme from '@/theme';
import { ExternalKeysProviders } from '@d-id/types';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import { CircularProgress, DialogActions, DialogContent, DialogTitle, MenuItem, Select } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

interface Props {
    onClose: () => void;
    onSave: (secretId: string) => void;
}

const DialogContainer = styled(BootstrapDialog)`
    & .MuiPaper-root {
        width: 598px;
        max-width: 598px;
        border-radius: 20px;
        border: 1.5px solid ${theme.colors.black[300]};
        box-shadow: 0px 25px 100px 0px rgba(28, 29, 33, 0.2);
    }
`;

const StyledDialogTitle = styled(DialogTitle)`
    padding: 24px !important;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: none;
    font-family: Manrope;
    font-size: 20px;
    font-weight: 600;
    line-height: normal;
    letter-spacing: -0.4px;
    color: ${theme.colors.secondary.main};
`;

const CloseButton = styled(IconButton)`
    width: 24px;
    height: 24px;
    padding: 0;
    min-width: 24px;
`;

const StyledDialogContent = styled(DialogContent)`
    padding: 0 22.5px;
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const FieldContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const FieldLabel = styled.label`
    font-family: Manrope;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    letter-spacing: -0.28px;
    color: ${theme.colors.secondary.main};
`;

const StyledTextField = styled(TextField)`
    & .MuiInputBase-root {
        border: 1px solid ${theme.colors.black[300]};
        border-radius: 8px;
        padding: 8px;
        font-family: Manrope;
        font-size: 12px;
        line-height: 16px;
        letter-spacing: 0.15px;
    }

    & .MuiInputBase-root.Mui-error {
        border-color: ${theme.colors.error.main};
    }

    & .MuiInputBase-input {
        padding: 0;
    }

    & .MuiOutlinedInput-notchedOutline {
        border: none;
    }
`;

const APIKeyInputContainer = styled.div<{ error?: boolean }>`
    display: flex;
    gap: 8px;
    align-items: center;
    border: 1px solid ${({ error }) => (error ? theme.colors.error.main : theme.colors.black[300])};
    border-radius: 8px;
    padding: 8px;
    height: 40px;
`;

const APIKeyInput = styled.input`
    flex: 1;
    border: none;
    outline: none;
    font-family: Manrope;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.15px;
    color: ${theme.colors.black.main};
    background: transparent;

    &::placeholder {
        color: ${theme.colors.black[500]};
    }
`;

const FetchModelsButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 8px;
    background-color: ${theme.colors.secondary.main};
    border: none;
    cursor: pointer;
    padding: 0;
    flex-shrink: 0;

    &:hover:not(:disabled) {
        background-color: ${theme.colors.secondary.hover};
    }

    &:disabled {
        cursor: not-allowed;
        background-color: ${theme.colors.secondary[500]};

        svg {
            color: ${theme.colors.white};
        }
    }
`;

const StyledSelect = styled(Select)`
    border: 1px solid ${theme.colors.black[300]};
    border-radius: 8px;
    font-family: Manrope;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.15px;
    color: ${theme.colors.black.main};
    cursor: pointer;

    & .MuiSelect-select {
        padding: 8px;
    }

    & .MuiOutlinedInput-notchedOutline {
        border: none;
    }

    &:hover {
        border-color: ${theme.colors.black[400]};
    }

    &.Mui-disabled {
        opacity: 0.5;
        background-color: ${theme.colors.black[100]};
    }

    & svg {
        right: 8px;
        color: ${theme.colors.secondary.main};
        top: unset;
    }
` as unknown as typeof Select;

const StyledMenuItem = styled(MenuItem)`
    &.Mui-selected {
        background-color: ${theme.colors.black[100]} !important;

        &:hover {
            background-color: ${theme.colors.black[100]} !important;
        }
    }
`;

const ToggleContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;
`;

const ToggleLabelContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
`;

const ToggleLabel = styled.label`
    font-family: Manrope;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    letter-spacing: -0.28px;
    color: ${theme.colors.secondary.main};
`;

const ToggleDescription = styled.p`
    font-family: Manrope;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    letter-spacing: 0.15px;
    color: ${theme.colors.black[700]};
    margin: 0;
`;

const MaxMessagesInputContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
`;

const MaxMessagesInputWrapper = styled.div`
    width: 85px;
`;

const StyledDialogActions = styled(DialogActions)`
    justify-content: flex-end !important;
    border-top: none;
    display: flex !important;
    width: 100%;
    margin-top: 24px;
`;

const AddButton = styled(Button)`
    min-width: 80px;
    height: 32px;
    font-family: Manrope;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    background-color: ${theme.colors.secondary.main} !important;
    color: ${theme.colors.white} !important;

    &.MuiButton-contained:disabled {
        background-color: ${theme.colors.secondary.main} !important;
        opacity: 0.5;
        color: ${theme.colors.white} !important;
    }

    &:hover:not(:disabled) {
        background-color: ${theme.colors.secondary.hover} !important;
    }

    & .MuiCircularProgress-root {
        color: ${theme.colors.white} !important;
    }
`;

const ErrorText = styled.p`
    font-family: Manrope;
    font-size: 12px;
    color: ${theme.colors.error.main};
    margin: 4px 0 0 0;
`;

interface OpenAIModel {
    id: string;
    object: string;
    created: number;
    owned_by: string;
}

interface OpenAIModelsResponse {
    data: OpenAIModel[];
    object: string;
}

export const AddCustomLLMModal = ({ onClose, onSave }: Props) => {
    const [baseUrl, setBaseUrl] = useState(DEFAULT_EXTERNAL_ENDPOINT);
    const [currentApiKey, setCurrentApiKey] = useState('');
    const [selectedModel, setSelectedModel] = useState('');
    const [isStreaming, setIsStreaming] = useState(true);
    const [maxMessagesEnabled, setMaxMessagesEnabled] = useState(false);
    const [maxMessages, setMaxMessages] = useState<number | undefined>(undefined);
    const [models, setModels] = useState<OpenAIModel[]>([]);
    const [isLoadingModels, setIsLoadingModels] = useState(false);
    const [errors, setErrors] = useState<{
        baseUrl?: string;
        apiKey?: string;
        model?: string;
        maxMessages?: string;
    }>({});

    const { mutateAsync: upsertSecret, isLoading: isSaving } = useUpsertSecret();

    const fetchModels = useCallback(async () => {
        if (!currentApiKey.trim() || !baseUrl.trim()) {
            return;
        }

        setIsLoadingModels(true);
        setErrors(prev => ({ ...prev, apiKey: undefined }));

        try {
            const response = await fetch(`${baseUrl.trim()}/models`, {
                headers: {
                    Authorization: `Bearer ${currentApiKey.trim()}`,
                },
            });

            if (!response.ok) {
                throw new Error(response.status === 401 ? 'Invalid API key' : 'Failed to fetch models');
            }

            const data: OpenAIModelsResponse = await response.json();

            const chatModels = data.data
                .filter((model: OpenAIModel) => model.id.includes('gpt') || model.id.includes('chat'))
                .sort((a: OpenAIModel, b: OpenAIModel) => a.id.localeCompare(b.id));

            setModels(chatModels);
            if (chatModels.length > 0) {
                setSelectedModel(chatModels[0].id);
            }
        } catch (error: any) {
            setErrors(prev => ({
                ...prev,
                apiKey: error.message === 'Invalid API key' ? 'Invalid API key' : 'Failed to fetch models',
            }));
            setModels([]);
        } finally {
            setIsLoadingModels(false);
        }
    }, [currentApiKey, baseUrl]);

    useEffect(() => {
        if (!currentApiKey.trim() || !baseUrl.trim()) {
            setModels([]);
            setSelectedModel('');
        }
    }, [currentApiKey, baseUrl]);

    const handleSave = async () => {
        try {
            const result = await upsertSecret({
                provider: ExternalKeysProviders.OpenAI,
                secret: currentApiKey.trim(),
                endpoint: baseUrl.trim(),
                model: selectedModel.trim(),
                isStreaming,
                maxMessages: maxMessagesEnabled ? maxMessages : undefined,
            });

            const secretId = result.id;
            if (secretId) {
                onSave(secretId);
            }
        } catch (error) {
            setErrors(prev => ({ ...prev, apiKey: 'Failed to save API key' }));
        }
    };

    const isFormValid =
        currentApiKey.trim() && selectedModel.trim() && (!maxMessagesEnabled || (maxMessages !== undefined && maxMessages > 0));

    return (
        <DialogContainer open={true} onClose={onClose} width="598px">
            <StyledDialogTitle>
                Add a Custom LLM (OpenAI)
                <CloseButton onClick={onClose} ariaLabel="Close">
                    <CloseIcon sx={{ fontSize: 24, color: theme.colors.secondary.main }} />
                </CloseButton>
            </StyledDialogTitle>

            <StyledDialogContent>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    <FieldContainer>
                        <FieldLabel>Base URL</FieldLabel>
                        <StyledTextField
                            value={baseUrl}
                            onChange={e => {
                                setBaseUrl(e.target.value);
                                setErrors(prev => ({ ...prev, baseUrl: undefined }));
                            }}
                            placeholder="https://api.openai.com/v1"
                            error={!!errors.baseUrl}
                            helperText={errors.baseUrl}
                        />
                    </FieldContainer>

                    <FieldContainer>
                        <FieldLabel>OpenAI API key</FieldLabel>
                        <APIKeyInputContainer error={!!errors.apiKey}>
                            <MaskedIcon src={Key} size="16px" style={{ flexShrink: 0 }} />
                            <APIKeyInput
                                type="text"
                                value={currentApiKey}
                                onChange={e => {
                                    setCurrentApiKey(e.target.value);
                                    setErrors(prev => ({ ...prev, apiKey: undefined }));
                                }}
                                onKeyPress={e => {
                                    if (e.key === 'Enter') {
                                        fetchModels();
                                    }
                                }}
                                onBlur={fetchModels}
                                placeholder="API key"
                            />
                            <FetchModelsButton
                                onClick={fetchModels}
                                type="button"
                                disabled={isLoadingModels || !currentApiKey.trim() || !baseUrl.trim()}>
                                {isLoadingModels ? (
                                    <CircularProgress size={16} sx={{ color: theme.colors.white }} />
                                ) : (
                                    <MaskedIcon src={ArrowLinearRight} size="16px" color={theme.colors.white} />
                                )}
                            </FetchModelsButton>
                        </APIKeyInputContainer>
                        {errors.apiKey && <ErrorText>{errors.apiKey}</ErrorText>}
                    </FieldContainer>

                    <FieldContainer>
                        <FieldLabel>Model</FieldLabel>
                        <StyledSelect
                            value={selectedModel}
                            onChange={e => {
                                setSelectedModel(e.target.value as string);
                                setErrors(prev => ({ ...prev, model: undefined }));
                            }}
                            disabled={isLoadingModels || models.length === 0}
                            IconComponent={props => (
                                <span {...props} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                    <MaskedIcon src={ArrowDown} size="16px" style={{ marginRight: '8px' }} />
                                </span>
                            )}
                            MenuProps={{
                                disablePortal: true,
                                PaperProps: {
                                    style: {
                                        width: 556,
                                        maxHeight: 220,
                                        borderRadius: '8px',
                                    },
                                },
                            }}
                            displayEmpty>
                            {models.map(model => (
                                <StyledMenuItem key={model.id} value={model.id}>
                                    {model.id}
                                </StyledMenuItem>
                            ))}
                        </StyledSelect>
                        {errors.model && <ErrorText>{errors.model}</ErrorText>}
                    </FieldContainer>

                    <ToggleContainer>
                        <ToggleLabelContainer>
                            <ToggleLabel>Streaming</ToggleLabel>
                            <ToggleDescription>
                                Enable if your custom LLM supports streaming responses. Ideal for lower latency.
                            </ToggleDescription>
                        </ToggleLabelContainer>
                        <Switch checked={isStreaming} onChange={e => setIsStreaming(e.target.checked)} />
                    </ToggleContainer>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <ToggleContainer>
                            <ToggleLabelContainer>
                                <ToggleLabel>Max messages</ToggleLabel>
                                <ToggleDescription>Number of messages to include as context in each request to the LLM.</ToggleDescription>
                            </ToggleLabelContainer>
                            <Switch
                                checked={maxMessagesEnabled}
                                onChange={e => {
                                    const enabled = e.target.checked;
                                    setMaxMessagesEnabled(enabled);
                                    if (enabled && maxMessages === undefined) {
                                        setMaxMessages(5);
                                    } else if (!enabled) {
                                        setMaxMessages(undefined);
                                    }
                                    setErrors(prev => ({ ...prev, maxMessages: undefined }));
                                }}
                            />
                        </ToggleContainer>
                        {maxMessagesEnabled && (
                            <MaxMessagesInputContainer>
                                <ToggleLabel>Messages</ToggleLabel>
                                <MaxMessagesInputWrapper>
                                    <StyledTextField
                                        type="number"
                                        value={maxMessages || ''}
                                        onChange={e => {
                                            const value = e.target.value ? parseInt(e.target.value, 10) : undefined;
                                            setMaxMessages(value);

                                            if (value !== undefined) {
                                                if (value < 1) {
                                                    setErrors(prev => ({ ...prev, maxMessages: 'Max messages must be greater than 0' }));
                                                } else if (value > 999) {
                                                    setErrors(prev => ({
                                                        ...prev,
                                                        maxMessages: 'The max allowed messages in each request is 999',
                                                    }));
                                                } else {
                                                    setErrors(prev => ({ ...prev, maxMessages: undefined }));
                                                }
                                            } else {
                                                setErrors(prev => ({ ...prev, maxMessages: undefined }));
                                            }
                                        }}
                                        placeholder="5"
                                        error={!!errors.maxMessages}
                                        inputProps={{ min: 1, max: 999 }}
                                    />
                                </MaxMessagesInputWrapper>
                                {errors.maxMessages && <ErrorText>{errors.maxMessages}</ErrorText>}
                            </MaxMessagesInputContainer>
                        )}
                    </div>
                </div>
            </StyledDialogContent>

            <StyledDialogActions>
                <AddButton variant="contained" onClick={handleSave} disabled={!isFormValid || isSaving} isLoading={isSaving}>
                    Add
                </AddButton>
            </StyledDialogActions>
        </DialogContainer>
    );
};
